import {Component, inject, OnInit, input, output, viewChild} from '@angular/core';
import {TranslocoPipe} from '@jsverse/transloco';
import {
  TenderInvitationPreviewControllerService,
  TenderInvitationPreviewMailUpsertRequest,
  TenderInvitationPreviewResponse,
  TenderPhaseType,
} from '@frontend/shared-api';
import {RichTextEditorComponent} from '../../../../../../../shared/components/rich-text-editor/rich-text-editor.component';
import {
  ProjectStepRichTextService,
  RichTextSection,
  RichTextSectionConfig,
} from '../../../../../../../shared/services/project-step-rich-text.service';
import {Observable} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {ProjectBaseStepComponent} from '../../../../project-base-step/project-base-step.component';
import {TabStatus} from '../../../../../../../shared/tabs/tab-status.model';

@Component({
  selector: 'app-step-9-3-rfd-mail-preview',
  providers: [ProjectStepRichTextService],
  imports: [TranslocoPipe, RichTextEditorComponent],
  templateUrl: './step-7-3-rfd-invitation-mail-preview.component.html',
})
export class Step93RfdMailPreviewComponent extends ProjectBaseStepComponent implements OnInit {
  private readonly tenderInvitationPreviewControllerService = inject(
    TenderInvitationPreviewControllerService,
  );
  readonly richTextService = inject(
    ProjectStepRichTextService<
      TenderInvitationPreviewMailUpsertRequest,
      TenderInvitationPreviewResponse
    >,
  );

  readonly preloadedData = input<TenderInvitationPreviewResponse>();
  readonly statusChange = output<TabStatus>();
  readonly richTextEditor = viewChild(RichTextEditorComponent);

  onStatusChange = () => {
    this.emitStatusChange();
  };

  get mailSections() {
    return this.richTextService.getSections();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.initializeRichTextService();
  }

  private initializeRichTextService(): void {
    const config: RichTextSectionConfig<
      TenderInvitationPreviewMailUpsertRequest,
      TenderInvitationPreviewResponse
    > = {
      sections: MAIL_PREVIEW_SECTIONS,
      fieldMap: MAIL_PREVIEW_FIELD_MAP,
      saveFunction: (projectId, payload) => this.save(projectId, payload),
      loadFunction: (projectId) =>
        this.tenderInvitationPreviewControllerService.getTenderInvitationPreview({
          projectId,
          phase: TenderPhaseType.RFD,
        }),
    };

    this.richTextService.initialize(this.projectId, config, this.preloadedData());
  }

  private save(
    projectId: number,
    richTextPayload: TenderInvitationPreviewMailUpsertRequest,
  ): Observable<TenderInvitationPreviewResponse> {
    return this.tenderInvitationPreviewControllerService
      .getTenderInvitationPreview({
        projectId,
        phase: TenderPhaseType.RFD,
      })
      .pipe(
        switchMap((existingData) => {
          const completePayload: TenderInvitationPreviewMailUpsertRequest = {
            ...existingData,
            ...richTextPayload,
            emailSubject: this.richTextEditor()?.currentLabel() || existingData.emailSubject,
          };
          return this.tenderInvitationPreviewControllerService.upsertMail({
            projectId,
            phase: TenderPhaseType.RFD,
            tenderInvitationPreviewMailUpsertRequest: completePayload,
          });
        }),
      );
  }

  getEmailSubjectForSection(): string {
    return this.preloadedData()?.emailSubject || '';
  }

  protected emitStatusChange(): void {
    const totalSections = this.richTextService.getTotalSectionsCount();
    const completedSections = this.richTextService.getCompletedSectionsCount();

    this.statusChange.emit({totalSections, completedSections});
  }
}

export const MAIL_PREVIEW_SECTIONS: RichTextSection[] = [
  {
    key: 'step_rfd_invitation_preview_mail_preview',
    content: '',
    tooltipKey: '',
    hasChanges: false,
  },
];

export const MAIL_PREVIEW_FIELD_MAP: Record<
  string,
  keyof TenderInvitationPreviewMailUpsertRequest
> = {
  step_rfd_invitation_preview_mail_preview: 'mailText',
};
